import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { takeSessionNotice } from '@crestrewards/api-client';
import { Button, Card, CardContent, Input, Label } from '@crestrewards/ui';
import { AlertCircle, Info } from 'lucide-react';

import useAuth from '../../shared/hooks/useAuth';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Field-level rules. Returns an object of messages; empty means valid. */
function validate({ email, password }) {
  const errors = {};

  if (!email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!EMAIL_PATTERN.test(email.trim())) {
    errors.email = 'Enter a valid email address.';
  }

  if (!password) {
    errors.password = 'Enter your password.';
  }

  return errors;
}

export default function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [values, setValues] = useState({ email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [sessionNotice, setSessionNotice] = useState('');
  const [pending, setPending] = useState(false);

  // Why the last session ended — set by api-client before it redirected here.
  // Read in an effect rather than a state initialiser so StrictMode's double
  // mount cannot consume the message before it is shown.
  //
  // A router `state.notice` takes precedence: it is set by the reset-password
  // page on success, and "sign in with your new password" is more useful than
  // whatever ended the previous session.
  useEffect(() => {
    const routed = location.state?.notice;
    if (routed) {
      setSessionNotice(routed);
      // Consume it, so a refresh does not replay a message about something
      // that happened several minutes ago.
      takeSessionNotice();
      return;
    }

    const notice = takeSessionNotice();
    if (notice) setSessionNotice(notice);
    // location.state is read once, on mount, on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already signed in — don't show a login form.
  if (status === 'authenticated') {
    return <Navigate to={location.state?.from ?? '/dashboard'} replace />;
  }

  function handleChange(field) {
    return (event) => {
      const { value } = event.target;
      setValues((current) => ({ ...current, [field]: value }));

      // Clear a field error as soon as the user starts fixing it.
      setFieldErrors((current) => {
        if (!current[field]) return current;
        const next = { ...current };
        delete next[field];
        return next;
      });
      setFormError('');
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (pending) return;

    const errors = validate(values);
    setFieldErrors(errors);
    setFormError('');
    if (Object.keys(errors).length > 0) return;

    // A fresh attempt supersedes the explanation of the previous session.
    setSessionNotice('');

    setPending(true);
    try {
      await login({ email: values.email.trim(), password: values.password });
      navigate(location.state?.from ?? '/dashboard', { replace: true });
    } catch (error) {
      // Always the normalised message from api-client, never the error object.
      setFormError(error.message);
      setValues((current) => ({ ...current, password: '' }));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          {/* One of the three sanctioned homes for the brand gradient. */}
          <span
            aria-hidden="true"
            className="grid h-11 w-11 place-items-center rounded-lg bg-brand-gradient text-lg font-bold text-white"
          >
            C
          </span>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground">
              CrestRewards Super Admin
            </h1>
            <p className="mt-1 text-meta text-muted-foreground">
              Sign in to manage partners, clients and platform settings.
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {sessionNotice && !formError ? (
                <div
                  role="status"
                  className="flex items-start gap-2 rounded-md border-hairline border-border bg-background px-3 py-2.5 text-meta text-muted-foreground"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{sessionNotice}</span>
                </div>
              ) : null}

              {formError ? (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border-hairline border-destructive/30 bg-destructive-tint px-3 py-2.5 text-meta text-destructive-on-tint"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  placeholder="you@crestrewards.com"
                  value={values.email}
                  onChange={handleChange('email')}
                  disabled={pending}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                />
                {fieldErrors.email ? (
                  <p id="email-error" className="text-label text-destructive-on-tint">
                    {fieldErrors.email}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-label text-muted-foreground underline-offset-4 hover:text-primary-hover hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={values.password}
                  onChange={handleChange('password')}
                  disabled={pending}
                  aria-invalid={Boolean(fieldErrors.password)}
                  aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                />
                {fieldErrors.password ? (
                  <p id="password-error" className="text-label text-destructive-on-tint">
                    {fieldErrors.password}
                  </p>
                ) : null}
              </div>

              <Button type="submit" className="w-full" disabled={pending}>
                {pending ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-label text-muted-foreground">
          Internal tool. Access is restricted to CrestRewards staff.
        </p>
      </div>
    </div>
  );
}
